import { useState } from 'react';
import { X, Download, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { contentApi, type ImportResult } from '../../api/content';

interface NotionImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  projectId: string;
}

export function NotionImportDialog({ open, onClose, onImported, projectId }: NotionImportDialogProps) {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleImport = async () => {
    setImporting(true);
    setError('');
    try {
      const res = await contentApi.importNotion(projectId);
      setResult(res);
      if (res.created > 0) onImported();
    } catch (err) {
      setError((err as Error).message || 'Failed to import from Notion');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-[440px]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line">
          <h2 className="text-lg font-semibold text-text">Import from Notion</h2>
          <button onClick={handleClose} className="text-subtle hover:text-muted">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {result ? (
            <div className="text-center py-4">
              {result.created > 0 ? (
                <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={40} />
              ) : (
                <AlertCircle className="mx-auto text-amber-500 mb-3" size={40} />
              )}
              <div className="text-lg font-semibold text-text mb-1">
                {result.created} of {result.total} items imported
              </div>
              {result.documents_created !== undefined && result.documents_created > 0 && (
                <div className="text-sm text-muted">{result.documents_created} documents created</div>
              )}
              {result.skipped > 0 && (
                <div className="text-sm text-muted">{result.skipped} skipped</div>
              )}
              {result.errors.length > 0 && (
                <div className="mt-3 text-left bg-red-50 rounded-lg p-3 max-h-32 overflow-auto">
                  {result.errors.map((err, i) => (
                    <div key={i} className="text-xs text-red-600">{err}</div>
                  ))}
                </div>
              )}
              <button
                onClick={handleClose}
                className="mt-4 px-4 py-2 bg-surface-subtle text-text rounded-xl font-medium text-sm hover:bg-surface-subtle"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-surface-subtle rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Download className="w-7 h-7 text-muted" />
                </div>
                <p className="text-sm text-muted">
                  Import content items from your Notion workspace. This will scan the configured
                  Notion directory and create content items for each page found.
                </p>
              </div>

              {error && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>
              )}

              <button
                onClick={handleImport}
                disabled={importing}
                className="w-full px-4 py-2.5 bg-coral-500 text-white rounded-xl font-medium text-sm
                  hover:bg-coral-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {importing ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download size={14} />
                    Import from Notion
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
